import React, { useEffect } from 'react';
import { toFlag } from 'pano-utils';
import { type EditConfigLanguage, EditConfigLanguageType } from '@types';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import ToggleButton from '@components/ToggleButton';
import Tip from '@components/Tip';
import useTourEditing from '@hooks/useTourEditing';
import { $editingLangFlag } from '@stores/editingStores';
import { updateEditingLang } from '@stores/langStores';
import LinkSetting from '../components/LinkSetting';
import PanelBase from '../PanelBase';
import type { PanelBaseProps } from '../types';

const items: DropdownItem<EditConfigLanguageType>[] = [
  {
    id: EditConfigLanguageType.cn,
    name: '中文',
  },
  { id: EditConfigLanguageType.en, name: '英文' },
  { id: EditConfigLanguageType.system, name: '跟随系统语言自动切换中/英文' },
];

function LanguageBase({
  defaultValue,
  onConfirm,
}: PanelBaseProps<EditConfigLanguage>) {
  const { state, setState, isChanged } = useTourEditing(defaultValue, {
    type: EditConfigLanguageType.cn,
    showButton: 0,
    relationUrl: '',
  });

  useEffect(() => {
    updateEditingLang(state?.type ?? null);
    return () => {
      updateEditingLang(null);
    };
  }, [state?.type]);

  useEffect(() => {
    $editingLangFlag.api.set(state?.showButton ?? null);
    return () => {
      $editingLangFlag.api.set(null);
    };
  }, [state?.showButton]);

  const handleConfirm = () => {
    if (state.type === EditConfigLanguageType.system) {
      onConfirm({ ...state, relationUrl: '' });
    } else {
      onConfirm({
        ...state,
        relationUrl: state?.showButton ? state.relationUrl : '',
      });
    }
  };

  const renderLinkSetting = () => {
    if (state.type === EditConfigLanguageType.system) return;
    if (!state?.showButton) return;
    const target = state.type === EditConfigLanguageType.en ? '中文' : '英文';
    return (
      <>
        <div className="mt-20 mb-10">{`关联${target}版作品地址：`}</div>
        <LinkSetting
          value={state.relationUrl}
          onChange={(val) =>
            setState((draft) => {
              draft.relationUrl = val;
            })
          }
        />
        <Tip className="mt-20">
          非必填，关联后在作品中点击语言切换按钮即可打开该关联作品
        </Tip>
      </>
    );
  };

  return (
    <PanelBase
      title={<Title>界面语言</Title>}
      isChanged={isChanged}
      onHelpClick={() => {}}
      onConfirm={handleConfirm}
    >
      <Collapse title="语言设置">
        <Dropdown
          items={items}
          selected={state.type}
          onChange={(id) =>
            setState((draft) => {
              draft.type = id;
            })
          }
        />
        <div className="mt-20 flex items-center justify-between">
          <div>显示语言切换按钮</div>
          <ToggleButton
            isOn={!!state.showButton}
            onToggle={(bool) =>
              setState((draft) => {
                draft.showButton = toFlag(bool);
              })
            }
          />
        </div>
        {renderLinkSetting()}
      </Collapse>
    </PanelBase>
  );
}

export default LanguageBase;
